// v0.9 battle replays: the fight for a borough plays out on the map. The armies march in,
// trade fire for a few rounds while their numbers tick down, and the winner gets a stamp.
// Everything shown comes from the server's battle record; the animation only paces it out.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useGame, fmt } from '../state.tsx';
import { NycMap, unitArt, BATTLE_SPOTS, BOROUGH_LABEL, type MapBorough } from './NycMap.tsx';
import { play, sequence } from '../fx/sound.ts';
import { getSettings } from '../fx/settings.ts';

type UnitMap = Record<string, number>;
interface Side { id: number | null; name: string; units: UnitMap; lost: UnitMap; helpers: { id: number; name: string; units: UnitMap; lost: UnitMap }[] }
export interface Replay { id?: number; boroughId: string; attacker: Side; defender: Side; attackPower: number; defensePower: number; won: boolean; siege: boolean; at?: number }

const ROUNDS = 5;
const MARCH_MS = 1100;
const ROUND_MS = 850;

/** Everyone on one side, merged by unit type. */
function total(side: Side, which: 'units' | 'lost'): UnitMap {
  const out: UnitMap = {};
  for (const m of [side[which], ...side.helpers.map((h) => h[which])]) for (const [k, n] of Object.entries(m)) out[k] = (out[k] ?? 0) + n;
  return out;
}
const sum = (m: UnitMap) => Object.values(m).reduce((s, n) => s + n, 0);
/** How many of each unit are left after `r` of ROUNDS rounds (losses come front-loaded a little). */
function leftAfter(start: UnitMap, lost: UnitMap, r: number): UnitMap {
  const f = r <= 0 ? 0 : r >= ROUNDS ? 1 : 1 - Math.pow(1 - r / ROUNDS, 1.4);
  const out: UnitMap = {};
  for (const [k, n] of Object.entries(start)) out[k] = n - Math.round((lost[k] ?? 0) * f);
  return out;
}

/** A cluster of unit tokens with counts, standing at (x, y). */
function Army({ units, start, x, y, side, phase, fading }: { units: UnitMap; start: UnitMap; x: number; y: number; side: 'atk' | 'def'; phase: number; fading: boolean }) {
  const kinds = Object.keys(start).filter((k) => start[k] > 0).sort((a, b) => start[b] - start[a]).slice(0, 4);
  const cols = kinds.length > 2 ? 2 : kinds.length;
  return (
    <g className={`army army-${side} ${fading ? 'army-beaten' : ''}`} transform={`translate(${x} ${y})`}>
      {kinds.map((k, i) => {
        const cx = (i % cols) * 40 - (cols * 40) / 2;
        const cy = Math.floor(i / cols) * 34 - 17;
        const left = units[k] ?? 0;
        const hit = phase > 0 && phase <= ROUNDS && left < start[k];
        return (
          <g key={k} transform={`translate(${cx} ${cy})`} className={hit ? 'token hit' : 'token'} style={{ animationDelay: `${(i * 90) % 300}ms` }}>
            <rect x="0" y="0" width="38" height="30" rx="3" className={`token-bg ${side}`} />
            <svg x="2" y="1" width="34" height="26" viewBox="0 0 64 48">{unitArt(k)}</svg>
            <rect x="20" y="20" width="20" height="12" rx="3" className="token-count-bg" />
            <text x="30" y="29.5" className="token-count">{left}</text>
          </g>
        );
      })}
    </g>
  );
}

export function BattleReplay({ replay, onClose }: { replay: Replay; onClose: () => void }) {
  const { content, me } = useGame();
  const [phase, setPhase] = useState(-1); // -1 = marching in, 0..ROUNDS = rounds, ROUNDS+1 = done
  const [run, setRun] = useState(0);
  const timers = useRef<number[]>([]);
  const def = content.boroughs.find((b: any) => b.id === replay.boroughId);
  const spots = BATTLE_SPOTS[replay.boroughId];
  const aStart = useMemo(() => total(replay.attacker, 'units'), [replay]);
  const aLost = useMemo(() => total(replay.attacker, 'lost'), [replay]);
  const dStart = useMemo(() => total(replay.defender, 'units'), [replay]);
  const dLost = useMemo(() => total(replay.defender, 'lost'), [replay]);
  const iAttacked = me && (replay.attacker.id === me.id || replay.attacker.helpers.some((h) => h.id === me.id));
  const iDefended = me && (replay.defender.id === me.id || replay.defender.helpers.some((h) => h.id === me.id));
  const goodForMe = (replay.won && iAttacked) || (!replay.won && iDefended);
  const badForMe = (replay.won && iDefended) || (!replay.won && iAttacked);

  useEffect(() => {
    for (const t of timers.current) clearTimeout(t);
    timers.current = [];
    const fast = getSettings().motion === 'off';
    if (fast) { setPhase(ROUNDS + 1); return; }
    setPhase(-1);
    play('drum');
    const at = (ms: number, fn: () => void) => timers.current.push(window.setTimeout(fn, ms));
    for (let r = 1; r <= ROUNDS; r++) {
      at(MARCH_MS + (r - 1) * ROUND_MS, () => {
        setPhase(r);
        sequence([[0, r % 2 ? 'gunshot' : 'punch'], [180, r === ROUNDS ? 'swords' : 'gunshot']]);
      });
    }
    at(MARCH_MS + ROUNDS * ROUND_MS + 300, () => {
      setPhase(ROUNDS + 1);
      // The winner stamp is drawn on the map itself (one stamp, not two).
      sequence(badForMe ? [[0, 'groan']] : [[0, 'horn'], [600, goodForMe ? 'cheer' : 'chime']]);
    });
    return () => { for (const t of timers.current) clearTimeout(t); };
  }, [run]);

  const skip = () => {
    for (const t of timers.current) clearTimeout(t);
    timers.current = [];
    setPhase(ROUNDS + 1);
  };
  const r = Math.max(0, Math.min(ROUNDS, phase));
  const aNow = leftAfter(aStart, aLost, r);
  const dNow = leftAfter(dStart, dLost, r);
  const done = phase > ROUNDS;
  const boroughs: MapBorough[] = content.boroughs.map((b: any) => ({
    id: b.id, name: b.name, ownerLabel: b.id === replay.boroughId ? (replay.siege ? 'SIEGE' : 'BATTLE') : '',
    fill: b.id === replay.boroughId ? (done ? (replay.won ? '#7a1a10' : '#8a6a10') : '#3a2a20') : '#2a2a2a',
  }));
  const L = BOROUGH_LABEL[replay.boroughId];
  const march = phase < 0;
  const atkX = spots.atk[0] + (march ? (spots.atk[0] - spots.def[0]) * 0.8 : 0);
  const atkY = spots.atk[1] + (march ? (spots.atk[1] - spots.def[1]) * 0.8 : 0);
  const flashes = phase >= 1 && phase <= ROUNDS;
  const powerBar = (p: number) => `${Math.round((p / Math.max(replay.attackPower, replay.defensePower, 1)) * 100)}%`;
  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal replay-modal" role="dialog" aria-label={`Battle for ${def?.name}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">{replay.siege ? 'Siege' : 'Battle'} for {def?.name}{replay.at ? <span className="small dim"> · {new Date(replay.at).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })}</span> : null}</div>
        <div className="modal-body">
          <div className="replay-map">
            <NycMap boroughs={boroughs} selected={replay.boroughId} onSelect={() => {}} size={600}>
              <g className="replay-layer" pointerEvents="none">
                {flashes && (
                  <g key={`f${phase}`} className="volley">
                    {[0, 1, 2].map((i) => (
                      <line key={i} x1={atkX + (i - 1) * 14} y1={atkY + (i - 1) * 8} x2={spots.def[0] + (1 - i) * 12} y2={spots.def[1] + (i - 1) * 10}
                        className="tracer" style={{ animationDelay: `${i * 90}ms` }} />
                    ))}
                    <circle cx={spots.def[0]} cy={spots.def[1]} r="16" className="burst" />
                    <circle cx={atkX} cy={atkY} r="12" className="burst" style={{ animationDelay: '220ms' }} />
                    {sum(dStart) - sum(dNow) > 0 && <text x={spots.def[0]} y={spots.def[1] - 36} className="loss-float">−{sum(leftAfter(dStart, dLost, r - 1)) - sum(dNow) || ''}</text>}
                    {sum(aStart) - sum(aNow) > 0 && <text x={atkX} y={atkY - 36} className="loss-float">−{sum(leftAfter(aStart, aLost, r - 1)) - sum(aNow) || ''}</text>}
                  </g>
                )}
                <Army units={dNow} start={dStart} x={spots.def[0]} y={spots.def[1]} side="def" phase={phase} fading={done && replay.won} />
                <g className="march" style={{ transform: `translate(${atkX - spots.atk[0]}px, ${atkY - spots.atk[1]}px)` }}>
                  <Army units={aNow} start={aStart} x={spots.atk[0]} y={spots.atk[1]} side="atk" phase={phase} fading={done && !replay.won} />
                </g>
                {done && (
                  <g transform={`translate(${L.x} ${L.y - 46})`}>
                    <g className="replay-stamp">
                      <rect x="-86" y="-22" width="172" height="50" rx="4" className={replay.won ? 'won' : 'held'} />
                      <text y="5" textAnchor="middle">{replay.won ? (replay.defender.id === null ? 'TAKEN' : 'CAPTURED') : 'HELD'}</text>
                      <text y="21" textAnchor="middle" className="stamp-sub">{(replay.won ? replay.attacker.name : replay.defender.name).slice(0, 24)}</text>
                    </g>
                  </g>
                )}
              </g>
            </NycMap>
          </div>
          <div className="replay-hud">
            <SideCard title="Attackers" side={replay.attacker} now={aNow} start={aStart} power={replay.attackPower} bar={powerBar(replay.attackPower)} cls="atk" winner={done && replay.won} />
            <div className="replay-vs">
              <div className="round">{phase < 0 ? 'Moving in…' : done ? 'Final' : `Round ${phase} of ${ROUNDS}`}</div>
              <div className="vs">VS</div>
              <div className="small dim">strength ±{content.rules.territory.luckPct}% luck</div>
            </div>
            <SideCard title={replay.defender.id === null ? 'Defenders' : 'Defenders'} side={replay.defender} now={dNow} start={dStart} power={replay.defensePower} bar={powerBar(replay.defensePower)} cls="def" winner={done && !replay.won} />
          </div>
          <div className="replay-buttons">
            {done ? <button className="btn gray" onClick={() => setRun((n) => n + 1)}>Watch again</button> : <button className="btn gray" onClick={skip}>Skip to the end</button>}
            <button className="btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SideCard({ title, side, now, start, power, bar, cls, winner }: { title: string; side: Side; now: UnitMap; start: UnitMap; power: number; bar: string; cls: string; winner: boolean }) {
  const { content } = useGame();
  const unitName = (k: string) => (k === 'gang' ? 'Gang members' : content.units.find((u: any) => u.id === k)?.name ?? k);
  return (
    <div className={`side-card ${cls} ${winner ? 'winner' : ''}`}>
      <div className="side-title">{title}{winner && <span className="win-tag">Winner</span>}</div>
      <div className="side-name">{side.name}{side.helpers.length > 0 && <span className="small"> + {side.helpers.map((h) => h.name).join(', ')}</span>}</div>
      <div className="side-power"><span>Strength <b>{fmt(power)}</b></span><div className="power-bar"><div style={{ width: bar }} /></div></div>
      <div className="side-units">
        {Object.keys(start).filter((k) => start[k] > 0).map((k) => (
          <div key={k} className="unit-line"><span>{unitName(k)}</span><b className={(now[k] ?? 0) < start[k] ? 'bad' : ''}>{now[k] ?? 0}</b><span className="dim">/ {start[k]}</span></div>
        ))}
        {Object.keys(start).length === 0 && <div className="dim small">No units.</div>}
      </div>
    </div>
  );
}
